import {Card, DataStatus} from 'platform/components';
import {Box, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {isNotNil, mergeAll} from 'ramda';

import {useGetEmployeeLinkQuery} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {DmsDataGrid, type DmsDataGridProps, Main, openActivityChangeLog} from '@dms/shared';

import {useRequiredParams} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['commandId', 'resource'] as const;

export function EmployeeActivityLogs(props: Pick<DmsDataGridProps, 'maximization'>) {
  const {id} = useRequiredParams();

  const {data: linkData, isLoading, isError} = useGetEmployeeLinkQuery({employeeId: id});

  const userId = linkData?.find((link) => link?.contextTarget === 'USER')?.contextId;
  const isEmployeeLinkedToUser = isNotNil(userId);

  const activityLogQueryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {user: userId}]),
    [userId]
  );

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = (data) => {
    const {actionKey, getColumnData} = data;
    const commandId = getColumnData('commandId')?.value;
    const resourceId = (getColumnData('resource')?.value as {key: string})?.key;

    match(actionKey).with('detail', () => {
      if (
        resourceId &&
        commandId &&
        typeof commandId === 'string' &&
        typeof resourceId === 'string'
      ) {
        openActivityChangeLog({
          resourceId: resourceId as EntityResourceIds,
          commandId,
          recordId: userId,
        });
      } else {
        console.error('no command in row');
      }
    });
  };

  return (
    <Main>
      <VStack spacing={4}>
        <Card title={i18n.t('general.labels.activityLogs')} isFullHeight>
          <DataStatus
            isLoading={isLoading}
            isError={isError}
            isEmpty={!isEmployeeLinkedToUser}
            emptyMessage={i18n.t('general.labels.noData')}
            emptySubheadline={i18n.t('entity.employee.labels.employeeNotLinkedToUser')}
            minHeight={100}
          >
            <Box>
              <DmsDataGrid
                maximization={props.maximization}
                actionCallback={actionCallback}
                predefinedColumns={PREDEFINED_COLUMNS}
                gridCode="by-user-audit-log"
                queryModifier={activityLogQueryModifier}
                autoHeight
                data-testid={testIds.employee.detail('activity-logs-datagrid')}
              />
            </Box>
          </DataStatus>
        </Card>
      </VStack>
    </Main>
  );
}
